import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";
interface DropdownItem {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  dropdown?: DropdownItem[];
}

const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Find Your Therapist", href: "/therapists" },
  {
    label: "Services",
    dropdown: [
      { label: "Individual Therapy", href: "/individual-therapy" },
      { label: "Child Therapy", href: "/child-therapy" },
      { label: "Teen Therapy", href: "/teen-therapy" },
      { label: "Couples Therapy", href: "/couples-therapy" },
      { label: "Group Therapy", href: "/group-therapy" },
    ],
  },
  {
    label: "Getting Started",
    dropdown: [
      { label: "How It Works", href: "/#how-it-works" },
      { label: "Rates & Insurance", href: "/full-pricing" },
      { label: "FAQ", href: "/#faq" },
      { label: "Contact", href: "/contact" },
    ],
  },
  { label: "Blog", href: "/blog" },
  { label: "Store", href: "https://thehealingspree.com" },
];

function DropdownMenu({ items, visible }: { items: DropdownItem[]; visible: boolean }) {
  return (
    <div
      className="absolute top-full mt-2 rounded-xl overflow-hidden transition-all duration-200"
      style={{
        left: "50%",
        transform: visible
          ? "translateX(-50%) translateY(0)"
          : "translateX(-50%) translateY(-6px)",
        minWidth: 210,
        background: "rgba(253,251,249,0.99)",
        border: "1px solid rgba(203,191,175,0.32)",
        boxShadow:
          "0 8px 32px -6px rgba(43,43,43,0.12), 0 2px 8px -2px rgba(43,43,43,0.06)",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        zIndex: 100,
      }}
    >
      <div className="py-1.5">
        {items.map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="flex items-center font-manrope text-[13.5px] font-medium text-[#2B2B2B]/80 hover:text-[#2B2B2B] hover:bg-[#2B2B2B]/[0.05] transition-all cursor-pointer px-4 py-2.5 whitespace-nowrap"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = useLocation();
  const isTherapistsIndex = location.pathname === "/therapists";
  const isOnLightPage = location.pathname.startsWith("/therapists/") || location.pathname === "/kinder-bradley";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const forceScrolled = isOnLightPage && !scrolled;
  const navOnLight = (isOnLightPage || isTherapistsIndex) && !scrolled;

  const pillBg = forceScrolled
    ? "rgba(255,255,255,0.10)"
    : scrolled
    ? "rgba(255,255,255,0.10)"
    : navOnLight
    ? "rgba(255,255,255,0.78)"
    : "rgba(255,255,255,0.13)";

  const pillBorder = forceScrolled
    ? "1px solid rgba(255,255,255,0.15)"
    : scrolled
    ? "1px solid rgba(255,255,255,0.15)"
    : navOnLight
    ? "1px solid rgba(43,43,43,0.12)"
    : "1px solid rgba(255,255,255,0.22)";

  const linkColor = navOnLight ? "rgba(43,43,43,0.70)" : "rgba(255,255,255,0.80)";
  const linkHoverColor = navOnLight ? "#2B2B2B" : "#fff";
  const linkHoverBg = navOnLight ? "rgba(43,43,43,0.08)" : "rgba(255,255,255,0.12)";

  const ctaBg = forceScrolled ? "#3C6FF8" : scrolled ? "#3C6FF8" : "rgba(60,111,248,0.85)";
  const ctaHoverBg = "#2B5BE0";

  const headerBg = forceScrolled
    ? "rgba(43,43,43,0.50)"
    : scrolled
    ? "rgba(43,43,43,0.50)"
    : isTherapistsIndex
    ? "rgba(251,249,246,0.84)"
    : "transparent";

  const headerBorder = forceScrolled
    ? "1px solid rgba(255,255,255,0.08)"
    : scrolled
    ? "1px solid rgba(255,255,255,0.08)"
    : isTherapistsIndex
    ? "1px solid rgba(43,43,43,0.08)"
    : "none";

  const logoColor = navOnLight ? "#2B2B2B" : "rgba(255,255,255,0.95)";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: headerBg,
        borderBottom: headerBorder,
        backdropFilter: forceScrolled || scrolled || isTherapistsIndex ? "blur(16px)" : "none",
      }}
    >
      <div
        className="w-full flex items-center justify-center px-6 md:px-10"
        style={{
          height: 34,
          borderBottom:
            forceScrolled || scrolled
              ? "1px solid rgba(255,255,255,0.06)"
              : navOnLight
              ? "1px solid rgba(43,43,43,0.10)"
              : "1px solid rgba(255,255,255,0.10)",
          background:
            forceScrolled || scrolled
              ? "rgba(0,0,0,0.18)"
              : navOnLight
              ? "rgba(43,43,43,0.06)"
              : "rgba(0,0,0,0.16)",
        }}
      >
        <span
          className="font-manrope text-[12px] font-bold tracking-wide"
          style={{ color: "#C4B5E8" }}
        >
          Servicing New York, New Jersey, and Indiana
        </span>
      </div>

      <nav className="w-full px-5 md:px-8 lg:px-14 h-[58px] grid grid-cols-3 items-center">
        <a href="/" className="flex items-center cursor-pointer">
          <span
            className="font-fraunces font-bold tracking-tight transition-colors duration-500 whitespace-nowrap"
            style={{
              fontSize: 16,
              color: logoColor,
              letterSpacing: "-0.01em",
            }}
          >
            Perspective Therapy
          </span>
        </a>

        <div className="hidden md:flex justify-center">
          <div
            className="flex items-center rounded-full transition-all duration-500"
            style={{
              background: pillBg,
              border: pillBorder,
              backdropFilter: "blur(10px)",
              padding: "3px 6px",
            }}
          >
            <ul className="flex items-center gap-0">
              {navItems.map((item) => (
                <li
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.dropdown && handleMouseEnter(item.label)}
                  onMouseLeave={() => item.dropdown && handleMouseLeave()}
                >
                  {item.dropdown ? (
                    <button
                      className="flex items-center gap-0.5 font-manrope text-[13px] font-medium transition-all tracking-wide whitespace-nowrap cursor-pointer px-2.5 py-1.5 rounded-full"
                      style={{
                        color: activeDropdown === item.label ? linkHoverColor : linkColor,
                        background: activeDropdown === item.label ? linkHoverBg : "transparent",
                      }}
                      onClick={() =>
                        setActiveDropdown(activeDropdown === item.label ? null : item.label)
                      }
                    >
                      {item.label}
                      <i
                        className={`ri-arrow-down-s-line text-[10px] ml-0.5 transition-transform duration-200 ${
                          activeDropdown === item.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <a
                      href={item.href}
                      className="font-manrope text-[13px] font-medium transition-all tracking-wide whitespace-nowrap cursor-pointer px-2.5 py-1.5 rounded-full block"
                      style={{ color: linkColor }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = linkHoverColor;
                        e.currentTarget.style.background = linkHoverBg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = linkColor;
                        e.currentTarget.style.background = "transparent";
                      }}
                      target={item.href?.startsWith("http") ? "_blank" : undefined}
                      rel={item.href?.startsWith("http") ? "nofollow" : undefined}
                    >
                      {item.label}
                    </a>
                  )}

                  {item.dropdown && (
                    <DropdownMenu items={item.dropdown} visible={activeDropdown === item.label} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="hidden md:flex justify-end items-center">
          <a
            href={ACTIVE_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-manrope text-[13.5px] font-semibold px-5 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer"
            style={{
              background: ctaBg,
              color: "#fff",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = ctaHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = ctaBg;
            }}
          >
            Book Now
            <i className="ri-arrow-right-line text-xs" />
          </a>
        </div>

        <div className="md:hidden col-span-2 flex justify-end">
          <button
            className="w-9 h-9 flex items-center justify-center cursor-pointer transition-colors"
            style={{ color: navOnLight ? "#2B2B2B" : "#fff" }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          className="md:hidden border-t px-5 py-4 flex flex-col gap-0.5 max-h-[82vh] overflow-y-auto"
          style={{
            background: "rgba(43,43,43,0.95)",
            borderColor: "rgba(255,255,255,0.10)",
          }}
        >
          <div
            className="py-2 mb-2 border-b text-center"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
          >
            <span className="font-manrope text-[12px] font-bold" style={{ color: "#C4B5E8" }}>
              Servicing New York, New Jersey, and Indiana
            </span>
          </div>

          {navItems.map((item) => (
            <div key={item.label}>
              {item.dropdown ? (
                <>
                  <button
                    className="w-full flex items-center justify-between font-manrope text-[15px] font-semibold text-white/85 hover:text-white transition-colors cursor-pointer py-2.5"
                    onClick={() =>
                      setMobileExpanded(mobileExpanded === item.label ? null : item.label)
                    }
                  >
                    {item.label}
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                        mobileExpanded === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {mobileExpanded === item.label && (
                    <div
                      className="pl-4 pb-2 flex flex-col gap-0 border-l-2 ml-1"
                      style={{ borderColor: "rgba(196,181,232,0.40)" }}
                    >
                      {item.dropdown.map((sub) => (
                        <a
                          key={sub.label}
                          href={sub.href}
                          onClick={() => setMenuOpen(false)}
                          className="font-manrope text-[14px] text-white/75 hover:text-[#C4B5E8] transition-colors cursor-pointer py-2"
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <a
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="block font-manrope text-[15px] font-semibold text-white/85 hover:text-white transition-colors cursor-pointer py-2.5"
                  target={item.href?.startsWith("http") ? "_blank" : undefined}
                  rel={item.href?.startsWith("http") ? "nofollow" : undefined}
                >
                  {item.label}
                </a>
              )}
            </div>
          ))}

          <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(203,191,175,0.22)" }}>
            <a
              href={ACTIVE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="inline-flex items-center justify-center gap-2 font-manrope text-[15px] font-semibold px-5 py-3 rounded-full cursor-pointer whitespace-nowrap w-full transition-all"
              style={{ background: "#3C6FF8", color: "#fff" }}
            >
              Book Now
              <i className="ri-arrow-right-line text-xs" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
